import { useCallback, useEffect, useRef } from "react";
import Slider, { SliderProps } from "@mui/material/Slider";

import useGetVideoInfo from "../../../hooks/useGetVideoInfo";
import formatTime from "../../../utils/formatTime";
import { useAppContext } from "../../../hooks/useAppContext";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

const SEEK_STEP = 1;

const VideoEditorCurrentTime: React.FC = () => {
  const { currentSeekTime, setCurrentSeekTime, videoRef, isProcessing } =
    useAppContext();

  const { currentTime, duration } = useGetVideoInfo();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // Ref to track rapid scrubbing
  const scrubTimestamps = useRef<number[]>([]);

  const handleTimelineChange = useCallback<
    NonNullable<SliderProps["onChange"]>
  >(
    (e, value) => {
      e.preventDefault();

      const seek = value as number;

      setCurrentSeekTime(seek);

      if (videoRef) {
        videoRef.currentTime = seek;
      }

      // 3. TELEMETRY: Frame Hunting Detection
      if (reportBehavior) {
        const now = Date.now();
        scrubTimestamps.current.push(now);
        
        // Keep only scrubs from the last 1.5 seconds
        scrubTimestamps.current = scrubTimestamps.current.filter(t => now - t < 1500);

        // If the slider updates rapidly (e.g., dragging back and forth repeatedly)
        if (scrubTimestamps.current.length > 20) {
          reportBehavior("frame_hunting_friction", 1, "behavior");
          scrubTimestamps.current = []; // Reset after reporting
        }
      }
    },
    [setCurrentSeekTime, videoRef, reportBehavior]
  );

  useEffect(() => {
    setCurrentSeekTime(currentTime);
  }, [currentTime, setCurrentSeekTime]);

  useEffect(() => {
    const seekOnArrowPress = (event: KeyboardEvent) => {
      // Prevent seeking if the user is typing in a text input field
      if (document.activeElement?.tagName === "INPUT") return;

      let nextStep = -1;

      if (event.key === "ArrowRight") {
        nextStep = currentSeekTime + SEEK_STEP;
      }

      if (event.key === "ArrowLeft") {
        nextStep = currentSeekTime - SEEK_STEP;
      }

      if (nextStep >= 0 && nextStep <= duration) {
        // TELEMETRY: User is utilizing high-precision keyboard controls
        if (reportBehavior) {
          reportBehavior("keyboard_precision_editing", 2, "interest");
        }
        
        // Pass a synthetic event to satisfy the Slider signature
        handleTimelineChange(event as any, nextStep, 0);
      }
    };

    window.addEventListener("keydown", seekOnArrowPress);

    return () => window.removeEventListener("keydown", seekOnArrowPress);
  }, [currentSeekTime, duration, handleTimelineChange, reportBehavior]);

  // 4. ADAPTIVE UI VARIABLES
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  // Dynamic colors replacing hardcoded #e20000 and #55aaff
  const playheadColor = isDarkMode ? "#00e5ff" : "#e20000";
  const markColor = isDarkMode ? "rgba(255, 255, 255, 0.2)" : "#55aaff";

  return (
    <Slider
      marks
      track={false}
      disabled={isProcessing}
      sx={{
        top: 8,
        height: 30,
        padding: "0 !important",
        borderRadius: 0,
        color: markColor,
        "& .MuiSlider-mark": {
          height: "6px",
          width: "1px",
          transform: "translate(0, -50%)",
        },
        "& .MuiSlider-mark:nth-of-type(10n)": {
          height: "10px",
        },
        "& .MuiSlider-thumb": {
          cursor: "col-resize",
          border: "none",
          top: 0,
          transform: "translateX(-50%)",
          "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
            boxShadow: "inherit",
          },
          height: 110,
          zIndex: 2,
          width: 10,
          backgroundColor: "transparent",
          borderRadius: 0,
          
          // ADAPTIVE UI: Pulse the playhead if the user is frustrated to improve visual tracking
          animation: highlightCTA && !isProcessing ? "pulse-playhead 2s infinite" : "none",

          "&:before": {
            display: "block",
            content: "''",
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: `5px solid ${playheadColor}`,
            width: 0,
            height: 0,
            top: 0,
            boxShadow: "none",
          },
          "&:after": {
            display: "block",
            content: "''",
            left: 4,
            top: 0,
            height: "100%",
            background: playheadColor,
            transform: "none",
            // ADAPTIVE UI: Thicken the playhead line if cognitive load is high
            width: highlightCTA ? 4 : 2, 
            borderRadius: 0,
          },
        },
        "& .MuiSlider-track": {
          borderRadius: "5px",
        },
        "@keyframes pulse-playhead": {
          "0%": { filter: `drop-shadow(0 0 0px ${playheadColor})` },
          "50%": { filter: `drop-shadow(0 0 6px ${playheadColor})` },
          "100%": { filter: `drop-shadow(0 0 0px ${playheadColor})` }
        }
      }}
      min={0}
      max={duration}
      step={SEEK_STEP}
      value={currentSeekTime}
      valueLabelDisplay="auto"
      valueLabelFormat={formatTime}
      onChange={handleTimelineChange}
    />
  );
};

export default VideoEditorCurrentTime;